import { Menu } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { BREAKPOINT_MOBILE, BREAKPOINT_TABLET, useMediaQuery } from '@/hooks/use-media-query';
import { useTemaEscuro } from '@/hooks/use-tema-escuro';
import { navegacao } from '@/navigation';
import { cn } from '@/lib/utils';
import { GlobalRequestProgress } from './global-request-progress';
import { PersonificacaoBanner } from './personificacao-banner';
import { Sidebar } from './sidebar';
import { UserChip } from './user-chip';

/**
 * AppShell de 3 zonas (Playbook § AppShell de 3 zonas).
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ Topbar 64px — marca · toggle · página · usuário   │
 *   ├───────────┬──────────────────────────────────────┤
 *   │ Sidebar   │ Conteúdo (bg-page)                   │
 *   │ 240/68px  │ rola independente                    │
 *   └───────────┴──────────────────────────────────────┘
 *
 * Montado uma vez e recebe `children`. Abaixo de 768px a sidebar vira drawer
 * off-canvas; entre 768 e 1279px entra colapsada (ícone-só) por padrão.
 */

interface AppShellProps {
  children: React.ReactNode;
  usuario: { nome: string; detalhe: string } | null;
  roles: string[];
}

export function AppShell({ children, usuario, roles }: AppShellProps) {
  const local = useLocation();
  const mobile = useMediaQuery(BREAKPOINT_MOBILE);
  const tablet = useMediaQuery(BREAKPOINT_TABLET);

  const [colapsadaManual, setColapsadaManual] = React.useState<boolean | null>(null);
  const [drawerAberto, setDrawerAberto] = React.useState(false);
  const [escuro, setEscuro] = useTemaEscuro();

  // Tablet colapsa por padrão; a escolha explícita do usuário prevalece.
  const colapsada = colapsadaManual ?? tablet;

  const paginaAtual = navegacao.find((item) => local.pathname.startsWith(item.rota));
  const tituloPagina = paginaAtual?.rotulo ?? 'Portal de Atualizações';

  React.useEffect(() => {
    setDrawerAberto(false);
  }, [local.pathname]);

  return (
    <div className="flex h-full flex-col bg-bg-page">
      <GlobalRequestProgress />
      <PersonificacaoBanner />

      {/* ---------- Topbar ---------- */}
      <header className="z-40 flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-bg-surface px-3 md:px-4">
        {mobile ? (
          <Button variant="ghost" size="icon" onClick={() => setDrawerAberto(true)} aria-label="Abrir menu de módulos">
            <Menu aria-hidden="true" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setColapsadaManual(!colapsada)}
            aria-label={colapsada ? 'Expandir menu lateral' : 'Recolher menu lateral'}
            aria-expanded={!colapsada}
          >
            <Menu aria-hidden="true" />
          </Button>
        )}

        {/* Marca */}
        <div className="flex items-center gap-2.5 pl-1">
          <span
            aria-hidden="true"
            className="flex size-8 shrink-0 items-center justify-center rounded-button bg-primary font-narrow text-[13px] font-bold tracking-wide text-on-primary"
          >
            PAN
          </span>
          <span className="hidden flex-col leading-tight sm:flex">
            <span className="text-[13px] font-bold">Portal de Atualizações</span>
            <span className="text-micro font-normal text-on-surface-muted">by Nasajon</span>
          </span>
        </div>

        <span className="mx-2 hidden h-7 w-px bg-border md:inline-block" aria-hidden="true" />

        {/* Eco visual do título da view, não um segundo <h1>: a página já tem o
            seu, e dois h1 quebrariam a hierarquia de cabeçalhos para o leitor
            de tela. Por isso fica fora da árvore de acessibilidade — mas é o
            elemento mais forte da topbar, porque é ele que responde "onde eu
            estou" quando a barra rola para fora de vista. */}
        <span
          aria-hidden="true"
          className="hidden min-w-0 flex-1 truncate text-[18px] font-bold leading-none tracking-[-0.01em] text-on-surface md:block"
        >
          {tituloPagina}
        </span>
        <div className="flex-1 md:hidden" />

        {usuario ? (
          <UserChip
            usuario={usuario}
            roles={roles}
            escuro={escuro}
            aoAlternarTema={() => setEscuro((valor) => !valor)}
          />
        ) : null}
      </header>

      {/* ---------- Corpo: sidebar + conteúdo ---------- */}
      <div className="flex min-h-0 flex-1">
        {!mobile ? (
          <aside
            className="shrink-0 border-r border-border transition-[width] ease-standard"
            style={{
              width: colapsada ? 'var(--size-sidebar-collapsed)' : 'var(--size-sidebar)',
              transitionDuration: 'var(--duration-base)',
            }}
          >
            <Sidebar colapsada={colapsada} />
          </aside>
        ) : (
          <Sheet open={drawerAberto} onOpenChange={setDrawerAberto}>
            <SheetContent side="left" className="w-[min(18rem,85vw)] gap-0 p-0">
              <SheetTitle className="border-b border-border px-4 py-4">Módulos do PAN</SheetTitle>
              <Sidebar colapsada={false} aoNavegar={() => setDrawerAberto(false)} />
            </SheetContent>
          </Sheet>
        )}

        {/* Conteúdo rola independente da sidebar. */}
        <main className={cn('pan-scroll-area min-w-0 flex-1 overflow-y-auto')}>{children}</main>
      </div>
    </div>
  );
}
